import { Container, Box, Paper, Typography, Breadcrumbs, Grid } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEOTags } from './SEOTags';
import { AdPlaceholder } from './AdPlaceholder';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BaseLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  breadcrumbs: BreadcrumbItem[];
  // When true, SEOTags removes the canonical link and skips hreflang emission
  // (used by the 404 route so a soft-404 URL is not self-canonicalized).
  // Absent for every normal page — no behavior change.
  suppressCanonical?: boolean;
}

export const BaseLayout = ({
  children,
  title,
  description,
  breadcrumbs,
  suppressCanonical,
}: BaseLayoutProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const langParam = new URLSearchParams(location.search).get('lang');
  const buildHref = (path: string) => (langParam ? `${path}?lang=${langParam}` : path);
  return (
    <Box className="app" component="main">
      <SEOTags title={title} description={description} suppressCanonical={suppressCanonical} />
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label={t('a11y.breadcrumb')}
          sx={{ mb: 3 }}
        >
          <Link to={buildHref('/')} style={{ color: 'inherit', textDecoration: 'none' }}>
            {t('footer.home')}
          </Link>
          {breadcrumbs.map((item, index) => {
            const isLast = index === breadcrumbs.length - 1;
            if (isLast || !item.path) {
              return (
                <Typography key={item.label} color={isLast ? 'text.primary' : 'text.secondary'}>
                  {item.label}
                </Typography>
              );
            }
            return (
              <Link
                key={item.label}
                to={buildHref(item.path)}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {item.label}
              </Link>
            );
          })}
        </Breadcrumbs>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={9}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 4 } }}>
              {children}
            </Paper>
          </Grid>
          <Grid item xs={12} lg={3}>
            <AdPlaceholder />
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
};
